<script>
import { createNamespacedHelpers } from "vuex"
import { TERMINAL_STACK_STATUSES } from "@/store/modules/subscription.js"

const { mapState, mapActions, mapMutations } = createNamespacedHelpers("subscription")

export default {
  name: "CSubscription",
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      default: () => "",
    },
  },
  emits: ["fetched", "created", "cancelled", "sanctionBlocked"],
  data() {
    return {
      isFetching: this.$store.state.subscription.subscriptions.length === 0,
      isLoading: false,
    }
  },
  computed: {
    ...mapState(["subscriptions"]),
    normalizedSubscriptions() {
      const dateDifferenceInDays = (a, b) => {
        const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())
        const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate())
        const msPerDay = 1000 * 60 * 60 * 24
        return Math.floor((utc2 - utc1) / msPerDay)
      }
      const toLocaleDateStringOptions = {
        day: "numeric",
        month: "short",
        year: "numeric",
      }

      return this.subscriptions.map(({
        paymentAt,
        expiresAt,
        subscriptionStatus,
        fqdn,
        stackId,
        totalDues,
        cancelReasonCode,
        actionAtTermEnd,
        contractTermEnd,
        planId,
        ...rest
      }) => {
        const isActiveCanceled = subscriptionStatus === "active"
          && (actionAtTermEnd === "cancel" || cancelReasonCode)
        const contractDaysLeft = contractTermEnd
          ? dateDifferenceInDays(new Date(Date.now()), new Date(contractTermEnd * 1000))
          : null
        const isMYPA = planId?.includes("paid_annually")
        const isCancellationBlocked = (() => {
          if (subscriptionStatus !== "active" || isActiveCanceled) return false
          const blockingAt = isMYPA ? (contractTermEnd ?? paymentAt) : paymentAt
          if (!blockingAt) return false
          const daysUntilBlocking = dateDifferenceInDays(
            new Date(Date.now()),
            new Date(blockingAt * 1000),
          )
          // for monthly subs, effective deletion date is next cycle end
          const isMonthly = planId?.includes("month")
          const effectiveDays = isMonthly ? daysUntilBlocking + 30 : daysUntilBlocking
          return effectiveDays <= 30
        })()
        return {
          ...rest,
          planId,
          subscriptionStatus: subscriptionStatus,
          fqdn,
          stackId,
          expiresAt,
          // In the last year of a Salesforce contract there is no next yearly payment (paymentAt is null),
          // so the next date to show is the renewal at the contract end.
          // Null rather than "Invalid Date" when neither is set: new Date(undefined * 1000) renders that
          // straight onto the card.
          nextPayment: (paymentAt ?? contractTermEnd)
            ? new Date((paymentAt ?? contractTermEnd) * 1000).toLocaleDateString(undefined, toLocaleDateStringOptions)
            : null,
          // Null rather than a large negative number: a paid subscription has no trial expiry, and
          // new Date(undefined * 1000) is the epoch.
          trialDaysLeft: expiresAt
            ? dateDifferenceInDays(new Date(Date.now()), new Date(expiresAt * 1000))
            : null,
          remainingDaysLeft: contractDaysLeft !== null
            ? contractDaysLeft
            : dateDifferenceInDays(new Date(Date.now()), new Date(expiresAt * 1000)),
          isCancelled: subscriptionStatus === "non_renewing" || isActiveCanceled,
          isActiveCanceled,
          isTrial: subscriptionStatus === "in_trial",
          isActive: subscriptionStatus === "active" && !isActiveCanceled,
          subdomain: fqdn?.match(/^[^.]+/)[0],
          totalDues: totalDues / 100 || null,
          contractDaysLeft,
          isCancellationBlocked,
          contractEndDate: contractTermEnd
            ? new Date(contractTermEnd * 1000).toLocaleDateString(undefined, toLocaleDateStringOptions)
            : null,
        }
      })
    },
    normalizedSubscriptionById() {
      return this.normalizedSubscriptions.find(({ id }) => id === this.id) || {}
    },
    getTrialSubscription() {
      return this.subscriptions.find(subscription => subscription.subscriptionStatus === "in_trial")
    },
    hasSubscriptions() {
      return this.subscriptions.length > 0
    },
    canHaveTrial() {
      return this.subscriptions.every(({ subscriptionStatus }) => subscriptionStatus !== "in_trial")
    },
    isComplete() {
      return this.normalizedSubscriptionById?.stackStatus === "ready"
    },
    inProgress() {
      return this.normalizedSubscriptionById?.stackStatus === "not_ready"
    },
    isFailed() {
      return this.normalizedSubscriptionById?.stackStatus === "failed"
    },
    isBlockedSanctionReview() {
      return this.normalizedSubscriptionById?.stackStatus === "blocked_sanction_review"
    },
    inUnknown() {
      return !(this.inProgress || this.isComplete || this.isFailed || this.isBlockedSanctionReview)
    },
  },
  async created() {
    await this.fetchSubscriptions()
    // Only an instance that is still being provisioned needs following; the list already says
    // where the others stand.
    if (this.id && !TERMINAL_STACK_STATUSES.has(this.normalizedSubscriptionById.stackStatus)) {
      await this.pollSubscription({ id: this.id })
    }
    this.isFetching = false
    this.$emit("fetched", this.normalizedSubscriptions, this.normalizedSubscriptionById)
  },
  methods: {
    ...mapActions([
      "pollSubscription",
      "fetchSubscriptions",
      "cancelSubscription",
      "cancelSalesforceTrial",
      "updateSubscription",
      "restoreSubscription",
    ]),
    ...mapMutations(["CLEAR_CACHE"]),
    ...mapActions({
      async createSubscription(dispatch, planSubdomainRegion) {
        this.isLoading = true
        const subscription = await dispatch("createSubscription", planSubdomainRegion)
        if (subscription) {
          if (subscription.stackStatus === "blocked_sanction_review") {
            this.$emit("sanctionBlocked", subscription)
          }
          else {
            this.$emit("created")
          }
        }
        this.isLoading = false
      },
      async createTrial(dispatch, planSubdomainRegion) {
        this.isLoading = true
        if (await dispatch("createTrial", planSubdomainRegion)) {
          this.$emit("created")
        }
        this.isLoading = false
      },
      async changeSubdomain(dispatch, subdomain) {
        this.isLoading = true
        await dispatch("changeSubdomain", {
          ...this.normalizedSubscriptionById,
          subdomain,
        })
        this.isLoading = false
      },
    }),
    async changeSubscription(pricingPlan, currentPricingPlan, cancelReasonCode) {
      const {
        id,
        planId,
        isCancelled,
        isActive,
        isTrial,
      } = this.normalizedSubscriptionById
      const canBeCancelledSalesforceTrial = id.startsWith("pphm-") && !isActive && isTrial
      const canBeCancelled = pricingPlan?.id === planId && (isActive || isTrial)
      const canBeRestored = pricingPlan?.id === planId && isCancelled
      const canBeChanged = pricingPlan?.id !== planId

      this.isLoading = true
      // have to copy because cancelSubscription may remove an item if it is a trial
      // and this.normalizedSubscription will return an empty object after calling it
      const copy = { ...this.normalizedSubscriptionById }
      if (canBeCancelledSalesforceTrial) {
        if (await this.cancelSalesforceTrial({ ...copy, cancelReasonCode })) {
          this.$emit("cancelled", copy)
        }
      }
      else if (canBeCancelled) {
        if (await this.cancelSubscription({ ...pricingPlan, ...copy, cancelReasonCode })) {
          this.$emit("cancelled", copy)
        }
      }
      else if (canBeRestored) {
        await this.restoreSubscription(copy)
      }
      else if (canBeChanged) {
        await this.updateSubscription({
          ...pricingPlan,
          ...this.normalizedSubscriptionById,
          planId: pricingPlan?.id,
          oldPlan: currentPricingPlan,
          newPlan: pricingPlan,
        })
      }
      this.isLoading = false
    },
  },
  render() {
    return this.$slots.default({
      subscriptions: this.normalizedSubscriptions,
      subscriptionById: this.normalizedSubscriptionById,
      trialSubscription: this.getTrialSubscription,
      isLoading: this.isLoading,
      isFetching: this.isFetching,
      isComplete: this.isComplete,
      inProgress: this.inProgress,
      isFailed: this.isFailed,
      isBlockedSanctionReview: this.isBlockedSanctionReview,
      inUnknown: this.inUnknown,
      hasSubscriptions: this.hasSubscriptions,
      canHaveTrial: this.canHaveTrial,
      changeSubscription: this.changeSubscription,
      createSubscription: this.createSubscription,
      createTrial: this.createTrial,
      changeSubdomain: this.changeSubdomain,
      clearCache: this.CLEAR_CACHE,
    })
  },
}
</script>
